// AGC: Settings - App configuration page (language, theme, biometric, security, etc.)
// AGC: Moved from Profile; accessible from left menu under Conta (Perfil, Planos, Configurações)

import React, { useState, useEffect } from 'react'
import { APP_VERSION } from '@droidfarm/shared'
import {
  Bell,
  Shield,
  Globe,
  Moon,
  Sun,
  Monitor,
  HelpCircle,
  ChevronRight,
  Fingerprint,
  Coins,
  Check,
  X,
  Languages,
  Volume2,
} from 'lucide-react'
import { TopBar, Card } from '../shared'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { useTheme, ACCENT_COLORS, type AccentColor } from '../../context/ThemeContext'
import { updateBiometricStatus } from '../../services/firebase-auth'
import { useBiometric } from '../../hooks/useBiometric'
import { isBiometricEnabled, setBiometricEnabled as setBiometricPref } from '../../services/biometricPreferences'
import { isFeedbackEnabled, setFeedbackEnabled } from '../../services/feedbackService'
import type { Screen } from '../../types'

interface SettingsProps {
  onBack: () => void
  onNavigate: (screen: Screen) => void
}

/**
 * AGC: Settings - User and app settings (language, theme, biometric, security, etc.)
 * AGC: Replaces the previous "coming soon" placeholder.
 */
export const Settings: React.FC<SettingsProps> = ({ onBack, onNavigate }) => {
  const { user } = useAuth()
  const {
    t,
    language,
    changeLanguage,
    availableLanguages,
    currency,
    currencyInfo,
    changeCurrency,
    availableCurrencies,
  } = useTranslation()
  const { theme, setTheme, isDark, accentColor, setAccentColor, accentConfig } = useTheme()
  const { isAvailable: isBiometricAvailable, enrollBiometric } = useBiometric()

  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [showCurrencyModal, setShowCurrencyModal] = useState(false)
  const [showThemeModal, setShowThemeModal] = useState(false)
  const [showAccentColorModal, setShowAccentColorModal] = useState(false)
  const [biometricEnabled, setBiometricEnabled] = useState(false)
  const [feedbackOn, setFeedbackOn] = useState(() => isFeedbackEnabled())
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // AGC: Sync biometric state from biometricPreferences (Yitix-style local lock)
  useEffect(() => {
    setBiometricEnabled(isBiometricEnabled())
  }, [])

  const currentLanguageInfo = availableLanguages.find((l) => l.code === language) || {
    name: 'English',
    flag: '🇺🇸',
  }
  const themeDisplayInfo = {
    light: { label: t('profile.lightMode') || 'Modo Claro', icon: '☀️' },
    dark: { label: t('profile.darkMode') || 'Modo Escuro', icon: '🌙' },
    system: { label: t('profile.systemMode') || 'Sistema', icon: '💻' },
  }

  const handleToggleBiometric = async () => {
    if (!user) return
    setIsSaving(true)
    setSaveError('')
    try {
      if (biometricEnabled) {
        await updateBiometricStatus(user.uid, false)
        setBiometricPref(false)
        setBiometricEnabled(false)
        localStorage.removeItem('droidfarm_biometric_credential')
        localStorage.removeItem('droidfarm_biometric_user')
      } else {
        if (isBiometricAvailable) {
          const success = await enrollBiometric(user.uid)
          if (success) {
            setBiometricPref(true)
            setBiometricEnabled(true)
          } else {
            setSaveError(t('profile.errors.biometricSetupFailed'))
          }
        } else {
          setSaveError(t('profile.errors.biometricNotAvailable'))
        }
      }
    } catch (error) {
      console.error('AGC: Error toggling biometric', error)
      setSaveError(t('profile.errors.biometricConfigError'))
    } finally {
      setIsSaving(false)
    }
  }

  const settingsItems = [
    {
      id: 'notifications',
      icon: <Bell className="w-5 h-5" />,
      label: t('profile.notifications'),
      description: t('profile.notifications'),
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      onClick: undefined as (() => void) | undefined,
      disabled: true,
    },
    {
      id: 'security',
      icon: <Shield className="w-5 h-5" />,
      label: t('profile.security'),
      description: t('profile.security'),
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      onClick: undefined as (() => void) | undefined,
      disabled: true,
    },
    {
      id: 'language',
      icon: <Globe className="w-5 h-5" />,
      label: t('profile.languageRegion'),
      description: `${currentLanguageInfo.flag} ${currentLanguageInfo.name}`,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      onClick: () => setShowLanguageModal(true),
      disabled: false,
    },
    {
      id: 'currency',
      icon: <Coins className="w-5 h-5" />,
      label: t('financial.currency') || 'Currency',
      description: `${currencyInfo.flag} ${currencyInfo.code} (${currencyInfo.symbol})`,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
      onClick: () => setShowCurrencyModal(true),
      disabled: false,
    },
    {
      id: 'theme',
      icon: isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />,
      label: t('profile.theme'),
      description: `${themeDisplayInfo[theme].icon} ${themeDisplayInfo[theme].label}`,
      color: 'text-gray-600 dark:text-gray-300',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
      onClick: () => setShowThemeModal(true),
      disabled: false,
    },
    // AGC: Accent color picker removed - using default theme only
    {
      id: 'help',
      icon: <HelpCircle className="w-5 h-5" />,
      label: t('profile.helpAndSupport'),
      description: t('profile.helpDescription'),
      color: 'text-amber-600',
      bgColor: 'bg-amber-100 dark:bg-amber-900/30',
      onClick: () => {
        // AGC: Open help center in user's language
        const helpUrls: Record<string, string> = {
          pt: 'https://droidfarm.com/ajuda',
          es: 'https://droidfarm.com/ayuda',
          en: 'https://droidfarm.com/help',
        }
        window.open(helpUrls[language] || helpUrls.en, '_blank')
      },
      disabled: false,
    },
  ]

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900">
      <TopBar
        title={t('common.settings')}
        onBack={onBack}
        className="bg-white dark:bg-slate-800 shadow-sm"
      />

      <div className="px-4 py-6 pb-24">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          {t('common.settings')}
        </h2>
        <Card padding="none">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {/* AGC: Biometric toggle */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                <Fingerprint className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.biometric')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {biometricEnabled ? t('profile.enabled') : t('profile.disabled')}
                </p>
              </div>
              <button
                onClick={handleToggleBiometric}
                disabled={isSaving || !isBiometricAvailable}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  biometricEnabled ? 'bg-[#0B5F4A] dark:bg-emerald-600' : 'bg-gray-300 dark:bg-slate-600'
                } ${isSaving || !isBiometricAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    biometricEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {saveError && (
              <p className="px-4 py-2 text-xs text-red-500 dark:text-red-400">{saveError}</p>
            )}
            {/* AGC: Click Feedback toggle - sound and haptics on button press */}
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300">
                <Volume2 className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.clickFeedback')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.clickFeedbackDesc')}
                </p>
              </div>
              <button
                onClick={() => {
                  const next = !feedbackOn
                  setFeedbackEnabled(next)
                  setFeedbackOn(next)
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  feedbackOn ? 'bg-[#0B5F4A] dark:bg-emerald-600' : 'bg-gray-300 dark:bg-slate-600'
                } cursor-pointer`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    feedbackOn ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {/* AGC: Sistema section - opens sub-page with system config */}
            <button
              type="button"
              onClick={() => onNavigate('sistema-settings')}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300">
                <Shield className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('profile.settingsSystem')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('profile.settingsSystemDesc', 'Configurações do sistema')}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </button>
            {settingsItems.map((item) => (
              <button
                key={item.id}
                onClick={item.onClick}
                disabled={item.disabled}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left ${
                  item.disabled ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-lg ${item.bgColor} flex items-center justify-center ${item.color}`}
                >
                  {item.icon}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              </button>
            ))}
          </div>
        </Card>

        {/* AGC: App version */}
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            DroidFarm v{APP_VERSION}
            <span className="mx-2">•</span>
            {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* AGC: Language modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <Languages className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('profile.languageRegion')}
                </h3>
              </div>
              <button
                onClick={() => setShowLanguageModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-2">
              {availableLanguages.map((lang) => {
                const isSelected = lang.code === language
                return (
                  <button
                    key={lang.code}
                    onClick={async () => {
                      await changeLanguage(lang.code as 'en' | 'pt' | 'es')
                      setShowLanguageModal(false)
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isSelected
                        ? 'bg-purple-50 dark:bg-purple-900/30 border-2 border-purple-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                    }`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <div className="flex-1 text-left">
                      <p
                        className={`font-medium ${
                          isSelected ? 'text-purple-700 dark:text-purple-400' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {lang.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {lang.code === 'en' && 'English'}
                        {lang.code === 'pt' && 'Português (Brasil)'}
                        {lang.code === 'es' && 'Español'}
                      </p>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('common.language')}: {currentLanguageInfo.flag} {currentLanguageInfo.name}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Currency modal */}
      {showCurrencyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                  <Coins className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('financial.currency') || 'Currency'}
                </h3>
              </div>
              <button
                onClick={() => setShowCurrencyModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-2">
              {availableCurrencies.map((curr) => {
                const isSelected = curr.code === currency
                return (
                  <button
                    key={curr.code}
                    onClick={() => {
                      changeCurrency(curr.code as 'BRL' | 'USD' | 'ARS' | 'EUR')
                      setShowCurrencyModal(false)
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isSelected
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-500'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                    }`}
                  >
                    <span className="text-2xl">{curr.flag}</span>
                    <div className="flex-1 text-left">
                      <p
                        className={`font-medium ${
                          isSelected ? 'text-emerald-700 dark:text-emerald-400' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {curr.code} ({curr.symbol})
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{curr.name}</p>
                    </div>
                    {isSelected && (
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('financial.currency') || 'Currency'}: {currencyInfo.flag} {currencyInfo.code} (
                {currencyInfo.symbol})
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Theme modal */}
      {showThemeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  {isDark ? (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('profile.theme')}
                </h3>
              </div>
              <button
                onClick={() => setShowThemeModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-2">
              <button
                onClick={() => {
                  setTheme('light')
                  setShowThemeModal(false)
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  theme === 'light'
                    ? 'bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                }`}
              >
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Sun className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 text-left">
                  <p
                    className={`font-medium ${
                      theme === 'light' ? 'text-amber-700 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {t('profile.lightMode') || 'Modo Claro'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Fundo claro para ambientes iluminados
                  </p>
                </div>
                {theme === 'light' && (
                  <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
              <button
                onClick={() => {
                  setTheme('dark')
                  setShowThemeModal(false)
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  theme === 'dark'
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                }`}
              >
                <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                  <Moon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 text-left">
                  <p
                    className={`font-medium ${
                      theme === 'dark' ? 'text-indigo-700 dark:text-indigo-400' : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {t('profile.darkMode') || 'Modo Escuro'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Fundo escuro para reduzir cansaço visual
                  </p>
                </div>
                {theme === 'dark' && (
                  <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
              <button
                onClick={() => {
                  setTheme('system')
                  setShowThemeModal(false)
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  theme === 'system'
                    ? 'bg-cyan-50 dark:bg-cyan-900/30 border-2 border-cyan-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                }`}
              >
                <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div className="flex-1 text-left">
                  <p
                    className={`font-medium ${
                      theme === 'system' ? 'text-cyan-700 dark:text-cyan-400' : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {t('profile.systemMode') || 'Sistema'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Segue a configuração do dispositivo
                  </p>
                </div>
                {theme === 'system' && (
                  <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </button>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('profile.theme')}: {themeDisplayInfo[theme].icon} {themeDisplayInfo[theme].label}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Accent color modal */}
      {showAccentColorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: accentConfig.lightBg }}
                >
                  <div className="w-5 h-5 rounded-full" style={{ backgroundColor: accentConfig.primary }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('profile.accentColor') || 'Accent Color'}
                </h3>
              </div>
              <button
                onClick={() => setShowAccentColorModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-2">
              {(Object.keys(ACCENT_COLORS) as AccentColor[]).map((colorKey) => {
                const colorConfig = ACCENT_COLORS[colorKey]
                const isSelected = colorKey === accentColor
                return (
                  <button
                    key={colorKey}
                    onClick={() => {
                      setAccentColor(colorKey)
                      setShowAccentColorModal(false)
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                      isSelected ? 'border-2' : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent'
                    }`}
                    style={{
                      borderColor: isSelected ? colorConfig.primary : 'transparent',
                      backgroundColor: isSelected ? colorConfig.lightBg : undefined,
                    }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden"
                      style={{
                        background: `linear-gradient(135deg, ${colorConfig.gradientStart} 0%, ${colorConfig.gradientMid} 50%, ${colorConfig.gradientEnd} 100%)`,
                      }}
                    >
                      <div className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium" style={{ color: isSelected ? colorConfig.darkText : undefined }}>
                        {colorConfig.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {colorKey === 'blue'
                          ? (t('profile.accentBlueDesc') || 'Navy blue, professional')
                          : (t('profile.accentGreenDesc') || 'Emerald green, natural')}
                      </p>
                    </div>
                    {isSelected && (
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colorConfig.primary }}
                      >
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('profile.currentAccent') || 'Current'}: {accentConfig.name}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
